import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Mail, Send, Settings, CheckCircle, XCircle, Loader2, Bell, Clock, AlertTriangle,
  Package, Wrench, FileText, Calendar, Users, Shield, BarChart3, AlertCircle,
  Timer, CalendarDays, CalendarRange, FileWarning, UserPlus, Database
} from 'lucide-react';

// Definirea tuturor tipurilor de alerte
const ALERT_TYPES = {
  critical: {
    label: 'Alerte Critice',
    color: 'bg-red-500',
    icon: AlertCircle,
    alerts: [
      { key: 'stock_depleted', label: 'Stoc epuizat', desc: 'Cand un articol ajunge la stoc 0', icon: Package },
      { key: 'equipment_down', label: 'Echipament defect/oprit', desc: 'Cand un echipament este marcat ca defect', icon: Wrench },
      { key: 'sla_critical', label: 'SLA depasit critic', desc: 'Tichete cu SLA depasit >24h', icon: Timer }
    ]
  },
  operational: {
    label: 'Alerte Operationale',
    color: 'bg-orange-500',
    icon: AlertTriangle,
    alerts: [
      { key: 'stock_low', label: 'Stoc sub minim', desc: 'Cand un articol scade sub stocul minim configurat', icon: Package },
      { key: 'sla_risk', label: 'SLA in risc', desc: 'Tichete cu mai putin de 4h pana la deadline', icon: Clock },
      { key: 'ticket_pending', label: 'Tichet neacceptat', desc: 'Tichete noi neacceptate de mai mult de 8h', icon: FileText },
      { key: 'revision_overdue', label: 'Revizie nefinalizata', desc: 'Revizii programate care nu au fost finalizate', icon: Calendar }
    ]
  },
  planning: {
    label: 'Alerte Planificare',
    color: 'bg-yellow-500',
    icon: CalendarDays,
    alerts: [
      { key: 'revision_tomorrow', label: 'Revizii maine', desc: 'Lista reviziilor programate pentru ziua urmatoare', icon: Calendar },
      { key: 'revision_week', label: 'Revizii saptamana viitoare', desc: 'Sumar revizii programate saptamana urmatoare', icon: CalendarRange },
      { key: 'warranty_expiring', label: 'Garantii care expira', desc: 'Echipamente cu garantia care expira in 30 zile', icon: Shield },
      { key: 'contract_expiring', label: 'Contracte furnizori', desc: 'Contracte cu furnizori care expira curand', icon: FileWarning }
    ]
  },
  reports: {
    label: 'Rapoarte',
    color: 'bg-green-500',
    icon: BarChart3,
    alerts: [
      { key: 'daily_summary', label: 'Sumar zilnic', desc: 'Raport zilnic cu activitatea din sistem', icon: FileText },
      { key: 'weekly_kpi', label: 'Raport saptamanal KPI', desc: 'Indicatori de performanta saptamanali', icon: BarChart3 },
      { key: 'monthly_stats', label: 'Raport lunar statistici', desc: 'Statistici lunare complete', icon: BarChart3 }
    ]
  },
  admin: {
    label: 'Administrative',
    color: 'bg-blue-500',
    icon: Users,
    alerts: [
      { key: 'user_created', label: 'Utilizator nou', desc: 'Notificare cand un utilizator nou este creat', icon: UserPlus },
      { key: 'equipment_modified', label: 'Modificari echipamente', desc: 'Modificari pe echipamente critice', icon: Wrench },
      { key: 'system_errors', label: 'Erori de sistem', desc: 'Notificari despre erori si probleme tehnice', icon: Database }
    ]
  }
};

// Stare initiala pentru toate alertele
const getDefaultAlertSettings = () => {
  const settings = {};
  Object.values(ALERT_TYPES).forEach(category => {
    category.alerts.forEach(alert => {
      settings[alert.key] = {
        enabled: ['stock_low', 'sla_risk', 'revision_overdue', 'stock_depleted'].includes(alert.key),
        schedule: 'daily'
      };
    });
  });
  return settings;
};

const MailSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  
  const [formData, setFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'MAINTIX',
    smtp_use_tls: true,
    auto_alerts_enabled: false,
    alert_schedule: 'daily'
  });

  const [alertSettings, setAlertSettings] = useState(getDefaultAlertSettings());
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchEmailLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mail-settings');
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          smtp_password: ''
        }));
        // Load alert settings if they exist
        if (response.data.alert_settings) {
          setAlertSettings(prev => ({
            ...prev,
            ...response.data.alert_settings
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching mail settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const response = await api.get('/notifications/email-logs');
      setEmailLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Include alert settings in the save
      const dataToSave = {
        ...formData,
        alert_settings: alertSettings
      };
      await api.put('/mail-settings', dataToSave);
      toast.success('Setarile au fost salvate!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  // Toggle individual alert
  const toggleAlert = (alertKey) => {
    setAlertSettings(prev => ({
      ...prev,
      [alertKey]: {
        ...prev[alertKey],
        enabled: !prev[alertKey]?.enabled
      }
    }));
  };

  // Toggle entire category
  const toggleCategory = (category) => {
    const categoryAlerts = ALERT_TYPES[category].alerts;
    const allEnabled = categoryAlerts.every(a => alertSettings[a.key]?.enabled);
    
    setAlertSettings(prev => {
      const newSettings = { ...prev };
      categoryAlerts.forEach(alert => {
        newSettings[alert.key] = {
          ...newSettings[alert.key],
          enabled: !allEnabled
        };
      });
      return newSettings;
    });
  };

  // Set schedule for alert
  const setAlertSchedule = (alertKey, schedule) => {
    setAlertSettings(prev => ({
      ...prev,
      [alertKey]: {
        ...prev[alertKey],
        schedule
      }
    }));
  };

  // Count enabled alerts
  const countEnabledAlerts = () => {
    return Object.values(alertSettings).filter(a => a?.enabled).length;
  };

  // Count enabled in category
  const countEnabledInCategory = (category) => {
    const categoryAlerts = ALERT_TYPES[category].alerts;
    return categoryAlerts.filter(a => alertSettings[a.key]?.enabled).length;
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Introduceti o adresa email pentru test');
      return;
    }
    
    try {
      setTesting(true);
      setTestResult(null);
      const response = await api.post('/mail-settings/test', { test_email: testEmail });
      setTestResult({ success: true, message: response.data.message });
      toast.success('Email de test trimis cu succes!');
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Eroare la trimiterea emailului' });
      toast.error('Eroare la trimiterea emailului de test');
    } finally {
      setTesting(false);
    }
  };

  const handleSendAlerts = async () => {
    try {
      setSendingAlerts(true);
      const response = await api.post('/notifications/send-email');
      toast.success(response.data.message);
      fetchEmailLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea alertelor');
    } finally {
      setSendingAlerts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mail-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-7 h-7 text-[#0052CC]" />
            Configurari Email
          </h1>
          <p className="text-slate-600 mt-1">Configureaza serverul SMTP pentru notificari email</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary h-12">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Settings className="w-5 h-5 mr-2" />}
          SALVEAZA SETARILE
        </Button>
      </div>

      <Tabs defaultValue="smtp" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3 h-11">
          <TabsTrigger value="smtp">Server SMTP</TabsTrigger>
          <TabsTrigger value="alerts">Alerte Automate</TabsTrigger>
          <TabsTrigger value="logs">Istoric Emailuri</TabsTrigger>
        </TabsList>

        {/* Tab: SMTP Settings */}
        <TabsContent value="smtp" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle>Configurare Server SMTP</CardTitle>
                <CardDescription>Introduceti datele serverului de email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Host SMTP *</Label>
                  <Input
                    value={formData.smtp_host}
                    onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="h-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Port SMTP</Label>
                    <Input
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Criptare</Label>
                    <Select
                      value={formData.smtp_use_tls ? 'tls' : (formData.smtp_port === 465 ? 'ssl' : 'none')}
                      onValueChange={(v) => {
                        if (v === 'ssl') {
                          setFormData({ ...formData, smtp_port: 465, smtp_use_tls: false });
                        } else if (v === 'tls') {
                          setFormData({ ...formData, smtp_port: 587, smtp_use_tls: true });
                        } else {
                          setFormData({ ...formData, smtp_use_tls: false });
                        }
                      }}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ssl">SSL (port 465)</SelectItem>
                        <SelectItem value="tls">STARTTLS (port 587)</SelectItem>
                        <SelectItem value="none">Fara criptare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Username (Email)</Label>
                  <Input
                    value={formData.smtp_username}
                    onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                    placeholder="utilizator@domeniu.ro"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Parola / App Password</Label>
                  <Input
                    type="password"
                    value={formData.smtp_password}
                    onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                    placeholder="••••••••"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">
                    Pentru Gmail, folositi un App Password generat din contul Google
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle>Identitate Expeditor</CardTitle>
                  <CardDescription>Cum vor aparea emailurile trimise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email expeditor</Label>
                    <Input
                      value={formData.smtp_from_email}
                      onChange={(e) => setFormData({ ...formData, smtp_from_email: e.target.value })}
                      placeholder="noreply@domeniu.ro"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nume expeditor</Label>
                    <Input
                      value={formData.smtp_from_name}
                      onChange={(e) => setFormData({ ...formData, smtp_from_name: e.target.value })}
                      placeholder="MAINTIX"
                      className="h-11"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle>Test Conexiune</CardTitle>
                  <CardDescription>Trimite un email de test pentru verificare</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email destinatar test</Label>
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@exemplu.ro"
                      className="h-11"
                    />
                  </div>
                  <Button
                    onClick={handleTest}
                    disabled={testing || !formData.smtp_host}
                    className="w-full"
                    variant="outline"
                  >
                    {testing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    TRIMITE EMAIL TEST
                  </Button>
                  
                  {testResult && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 ${
                      testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                      <span className="text-sm">{testResult.message}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Alerte Automate */}
        <TabsContent value="alerts" className="mt-6">
          {/* Header cu sumar */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#0052CC] flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Configurare Alerte Email</h3>
                  <p className="text-sm text-slate-600">
                    {countEnabledAlerts()} din 17 alerte activate
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border">
                  <Switch
                    checked={formData.auto_alerts_enabled}
                    onCheckedChange={(v) => setFormData({ ...formData, auto_alerts_enabled: v })}
                  />
                  <span className="text-sm font-medium">
                    {formData.auto_alerts_enabled ? 'Activ' : 'Inactiv'}
                  </span>
                </div>
                <Button
                  onClick={handleSendAlerts}
                  disabled={sendingAlerts || !formData.smtp_host}
                  size="sm"
                  className="btn-primary"
                >
                  {sendingAlerts ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  TRIMITE ACUM
                </Button>
              </div>
            </div>
          </div>

          {/* Frecventa globala */}
          {formData.auto_alerts_enabled && (
            <Card className="card-industrial mb-6">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Frecventa implicita alerte</Label>
                    <p className="text-sm text-slate-500">Cand sa fie trimise alertele</p>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { value: 'realtime', label: 'Imediat', icon: AlertTriangle },
                      { value: 'hourly', label: 'La ora', icon: Clock },
                      { value: 'daily', label: 'Zilnic', icon: Calendar }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setFormData({ ...formData, alert_schedule: opt.value })}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2 ${
                          formData.alert_schedule === opt.value
                            ? 'border-[#0052CC] bg-blue-50 text-[#0052CC]'
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Categorii de alerte */}
          <div className="space-y-4">
            {Object.entries(ALERT_TYPES).map(([categoryKey, category]) => {
              const CategoryIcon = category.icon;
              const enabledCount = countEnabledInCategory(categoryKey);
              const totalCount = category.alerts.length;
              const allEnabled = enabledCount === totalCount;
              
              return (
                <Card key={categoryKey} className="card-industrial overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center`}>
                          <CategoryIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{category.label}</CardTitle>
                          <CardDescription>
                            {enabledCount} din {totalCount} active
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={enabledCount > 0 ? 'default' : 'secondary'}>
                          {enabledCount}/{totalCount}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleCategory(categoryKey)}
                          className="text-xs"
                        >
                          {allEnabled ? 'Dezactiveaza toate' : 'Activeaza toate'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid gap-2">
                      {category.alerts.map(alert => {
                        const AlertIcon = alert.icon;
                        const isEnabled = alertSettings[alert.key]?.enabled;
                        
                        return (
                          <div
                            key={alert.key}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                              isEnabled 
                                ? 'bg-blue-50/50 border-blue-200' 
                                : 'bg-slate-50/50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isEnabled ? 'bg-blue-100' : 'bg-slate-100'
                              }`}>
                                <AlertIcon className={`w-4 h-4 ${
                                  isEnabled ? 'text-blue-600' : 'text-slate-400'
                                }`} />
                              </div>
                              <div>
                                <p className={`font-medium text-sm ${
                                  isEnabled ? 'text-slate-900' : 'text-slate-500'
                                }`}>
                                  {alert.label}
                                </p>
                                <p className="text-xs text-slate-500">{alert.desc}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {isEnabled && (
                                <select
                                  value={alertSettings[alert.key]?.schedule || 'daily'}
                                  onChange={(e) => setAlertSchedule(alert.key, e.target.value)}
                                  className="text-xs border rounded-md px-2 py-1 bg-white"
                                >
                                  <option value="realtime">Imediat</option>
                                  <option value="hourly">Ora</option>
                                  <option value="daily">Zilnic</option>
                                  <option value="weekly">Saptamanal</option>
                                </select>
                              )}
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => toggleAlert(alert.key)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab: Istoric Emailuri */}
        <TabsContent value="logs" className="mt-6">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Istoric Emailuri Trimise</CardTitle>
              <CardDescription>Ultimele notificari email trimise din sistem</CardDescription>
            </CardHeader>
            <CardContent>
              {emailLogs.length > 0 ? (
                <div className="space-y-3">
                  {emailLogs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          log.status === 'sent' ? 'bg-green-100' : log.status === 'simulated' ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          {log.status === 'sent' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : log.status === 'simulated' ? (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{log.subject || 'Alerta Stoc'}</p>
                          <p className="text-xs text-slate-500">
                            {log.recipients?.length || 0} destinatari • {log.alerts_count || 0} alerte
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={log.status === 'sent' ? 'default' : log.status === 'simulated' ? 'secondary' : 'destructive'}>
                          {log.status === 'sent' ? 'Trimis' : log.status === 'simulated' ? 'Simulat' : 'Eroare'}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(log.sent_at).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nu exista emailuri trimise</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MailSettingsPage;
